import { Route } from 'react-router-dom';
import {
  EntitlementRouteGuard,
  SurfaceUnavailableScreen,
} from '../../features/entitlements/customerEntitlementGuards.js';
import { FeatureFlagRouteGuard } from '../../features/entitlements/FeatureFlagRouteGuard.js';
import { DonateAuthenticatedRedirect } from '../../features/donation/DonateAuthenticatedRedirect.js';
import { RequireSelfScanAuth } from '../../features/self-scan/routing/RequireSelfScanAuth.js';
import { PhoneFirstDonationJourneyLifecycle } from '../../features/journeys/components/TierAJourneyLifecycle.js';
import { ScreenLifecycle } from '../../shared/screen/ScreenLifecycle.js';
import {
  RemountBoundary,
  RouteErrorFallback,
} from '../../shared/components/ErrorBoundary/index.js';
import { ErrorIsolationProbe } from '../../test/e2e/errorIsolationProbe.js';
import { TenantAccountLegacyRedirectRoutes } from './account.js';
import {
  FindOrderPage,
  HelpPage,
  MoreRouteRedirect,
  ScannerPage,
  SelfScanScannerPage,
  ShopScreen,
} from './lazyRouteComponents.js';
import { withRegisteredScreen } from './registryGuard.js';
import { Wave1Suspense } from './routeHelpers.js';

/** L3 shop remount + retail chaos probe — shared by `/shop` and `/:kioskSlug`. */
export function withShopRemount(element: JSX.Element): JSX.Element {
  return (
    <RemountBoundary
      feature="shop"
      fallback={({ onRetry, feature }) => (
        <RouteErrorFallback
          onRetry={onRetry}
          feature={feature}
          testId="customer-eb-l3-shop"
        />
      )}
    >
      <ErrorIsolationProbe feature="retail" />
      {element}
    </RemountBoundary>
  );
}

/**
 * Residual G1 — Self-Scan entry under Skenovat (`/shop/scanner`) and resume
 * deep-links (`/self-scan`, `/self-scan/:basketPublicId`). Auth-gated; park via
 * RequireSelfScanAuth → same tenant/SP Self-Scan returnTarget.
 */
function withSelfScanAuth(element: JSX.Element): JSX.Element {
  return <RequireSelfScanAuth>{element}</RequireSelfScanAuth>;
}

function withDonateRemount(element: JSX.Element): JSX.Element {
  return (
    <RemountBoundary
      feature="donate"
      fallback={({ onRetry, feature }) => (
        <RouteErrorFallback
          onRetry={onRetry}
          feature={feature}
          testId="customer-eb-l3-donate"
        />
      )}
    >
      {element}
    </RemountBoundary>
  );
}

function moreHubRouteElement(): JSX.Element {
  return withRegisteredScreen(
    'MS-HUB-024',
    <Wave1Suspense>
      <MoreRouteRedirect />
    </Wave1Suspense>,
  );
}

/**
 * Find-order hub — EntitlementRouteGuard still wraps; on identity-only hub the guard
 * allows through without inventing home jwt (see isIdentityOnlyEntitlementIdle).
 * Tenant slug mounts keep order_pickup_infrastructure enforcement.
 */
function findOrderHubRouteElement(): JSX.Element {
  return withRegisteredScreen(
    // MS-PICK-041 = FindOrderPage (masterScreenEntries); MS-ORD-055 is pickup-ack embedded step.
    // Optional pickup UX only — not a primary bottom-nav tab.
    'MS-PICK-041',
    <EntitlementRouteGuard blockKeys={['order_pickup_infrastructure']}>
      <Wave1Suspense>
        <FindOrderPage />
      </Wave1Suspense>
    </EntitlementRouteGuard>,
  );
}

function helpHubRouteElement(): JSX.Element {
  return withRegisteredScreen(
    // MS-HUB-030 = HelpPage (More overflow; peer of Find Order MS-PICK-041).
    // Not MS-SHP-043 (Shop Help Modal). 028/029 reserved ADR-018 Welcome/History.
    'MS-HUB-030',
    <Wave1Suspense>
      <HelpPage />
    </Wave1Suspense>,
  );
}

/**
 * Wave D — platform bare hubs (`/more`, `/orders/find`, `/help`) under
 * PersistentRetailLayout + `__platform__` TenantProvider. Same page components
 * as tenant-relative mounts in {@link TenantRetailRoutes}.
 */
export function PlatformRetailHubRoutes(): JSX.Element {
  return (
    <>
      <Route path="more" element={moreHubRouteElement()} />
      <Route path="orders/find" element={findOrderHubRouteElement()} />
      <Route path="help" element={helpHubRouteElement()} />
    </>
  );
}

export function TenantRetailRoutes(options?: { includeMore?: boolean }): JSX.Element {
  const includeMore = options?.includeMore === true;

  return (
    <>
      {includeMore ? <Route path="more" element={moreHubRouteElement()} /> : null}
      {/*
        Shop / scanner always mount under retail chrome. Do not gate on
        product_vending — Obchody directory excludes unlistable tenants instead.
        Residual G1 — Self-Scan mode under Skenovat is auth-gated (no 4th nav).
      */}
      {/*
        Guest shop scanner stays open. Self-Scan mode (`?selfScan=1`) is auth-gated
        inside ScannerPage. Dedicated `/self-scan` resume routes are auth-gated here.
      */}
      <Route
        path="shop/scanner"
        element={withRegisteredScreen(
          'MS-HUB-021',
          withShopRemount(
            <Wave1Suspense>
              <ScannerPage />
            </Wave1Suspense>,
          ),
        )}
      />
      <Route
        path="self-scan/:basketPublicId"
        element={withRegisteredScreen(
          'MS-HUB-021',
          withShopRemount(
            withSelfScanAuth(
              <Wave1Suspense>
                <SelfScanScannerPage />
              </Wave1Suspense>,
            ),
          ),
        )}
      />
      <Route
        path="self-scan"
        element={withRegisteredScreen(
          'MS-HUB-021',
          withShopRemount(
            withSelfScanAuth(
              <Wave1Suspense>
                <SelfScanScannerPage />
              </Wave1Suspense>,
            ),
          ),
        )}
      />
      <Route
        path="shop"
        element={withRegisteredScreen(
          'MS-HUB-020',
          withShopRemount(
            <Wave1Suspense>
              <ScreenLifecycle screenId="MS-HUB-020">
                <ShopScreen />
              </ScreenLifecycle>
            </Wave1Suspense>,
          ),
        )}
      />
      <Route path="orders/find" element={findOrderHubRouteElement()} />
      <Route path="help" element={helpHubRouteElement()} />
      <Route
        path="donate"
        element={withRegisteredScreen(
          'MS-COM-035',
          withDonateRemount(
            <EntitlementRouteGuard blockKeys={['donation']}>
              <FeatureFlagRouteGuard requires={{ master: true, anyOf: ['donate'] }}>
                <DonateAuthenticatedRedirect />
              </FeatureFlagRouteGuard>
            </EntitlementRouteGuard>,
          ),
        )}
      >
        <Route
          path="projects"
          element={
            <Wave1Suspense>
              <PhoneFirstDonationJourneyLifecycle />
            </Wave1Suspense>
          }
        />
        <Route
          path=":donationProjectCode"
          element={
            <Wave1Suspense>
              <PhoneFirstDonationJourneyLifecycle />
            </Wave1Suspense>
          }
        />
        <Route
          index
          element={
            <Wave1Suspense>
              <PhoneFirstDonationJourneyLifecycle />
            </Wave1Suspense>
          }
        />
      </Route>
      <Route
        path="unavailable"
        element={withRegisteredScreen(
          'MS-ENTRY-002',
          <Wave1Suspense>
            <SurfaceUnavailableScreen />
          </Wave1Suspense>,
        )}
      />
      {/* Wave C — tenant account URLs hard-redirect to platform `/account/*`. */}
      {TenantAccountLegacyRedirectRoutes()}
    </>
  );
}
